/**
 * Auth Service - API methods for authentication
 */

import apiClient from './apiClient';
import type { User } from '../../stores/authStore';

// Refresh token request/response types
export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

// Complete onboarding types
export interface CompleteOnboardingRequest {
  accountType: 'INDIVIDUAL' | 'ORGANIZATION';
}

export interface OnboardingResponse {
  message: string;
  organizationId: string;
}

/**
 * Logout and revoke refresh token on server
 */
export const logout = async (refreshToken?: string): Promise<void> => {
  try {
    await apiClient.post('/api/v1/auth/logout', { refreshToken: refreshToken ?? '' });
  } catch (error) {
    // Even if logout fails on server, we'll clear local state
    console.error('Logout error:', error);
  }
};

/**
 * Refresh access token
 */
export const refreshToken = async (data: RefreshTokenRequest): Promise<RefreshTokenResponse> => {
  const response = await apiClient.post<RefreshTokenResponse>('/api/v1/auth/refresh', data);
  return response.data;
};

/**
 * Get current user profile
 */
export const getCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get<User>('/api/v1/auth/me');
  return response.data;
};

/**
 * Complete onboarding - provisions personal org + FREE plan for individual users.
 * Organization onboarding is platform-admin only.
 */
export const completeOnboarding = async (data: CompleteOnboardingRequest): Promise<OnboardingResponse> => {
  const response = await apiClient.post<OnboardingResponse>('/api/v1/auth/complete-onboarding', data);
  return response.data;
};

export interface OrgSelectRequest {
  orgSelectToken: string;
  orgId: string;
}

export interface OrgSelectResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

/**
 * Exchange an org-select token for a full session (multi-org users only).
 */
export const selectOrg = async (data: OrgSelectRequest): Promise<OrgSelectResponse> => {
  const response = await apiClient.post<OrgSelectResponse>('/api/v1/auth/entra/select-org', data);
  return response.data;
};

/**
 * Build the redirect URL for Entra auth initiation.
 * Not an API call — returns the URL string for window.location.assign().
 */
export const entraRedirectUrl = (
  opts?: { invitationToken?: string; returnTo?: string },
): string => {
  const params = new URLSearchParams();
  if (opts?.invitationToken) params.set('invitationToken', opts.invitationToken);
  if (opts?.returnTo) params.set('returnTo', opts.returnTo);
  const qs = params.toString();
  return `/api/v1/auth/entra/redirect${qs ? `?${qs}` : ''}`;
};

/**
 * Build the Entra end-session (logout) URL. Not an API call — returns a URL for
 * window.location.assign() so the browser does a full-page redirect through Entra's
 * logout endpoint, clearing the Entra SSO cookie before returning to the app.
 * `returnTo` is an app-relative path the browser lands on afterwards (default /login).
 */
export const entraLogoutUrl = (returnTo = '/login'): string =>
  `/api/v1/auth/entra/logout?returnTo=${encodeURIComponent(returnTo)}`;

// Legacy methods — kept for pages that exist but are no longer in the router.
// Remove these when those page files are deleted.

export interface LoginRequest { email: string; password: string; }
export interface LoginResponse { user: User; accessToken: string; refreshToken: string; }
export interface ChangePasswordRequest { currentPassword: string; newPassword: string; }
export interface CheckEmailResponse { exists: boolean; }
export interface SignupRequest { firstName: string; lastName: string; email: string; password: string; }
export interface SignupResponse { message: string; verificationLink: string; }

export const login = async (data: LoginRequest): Promise<LoginResponse> => {
  const response = await apiClient.post<LoginResponse>('/api/v1/auth/login', data);
  return response.data;
};
export const changePassword = async (data: ChangePasswordRequest): Promise<void> => {
  await apiClient.post('/api/v1/auth/change-password', data);
};
export const checkEmail = async (email: string): Promise<CheckEmailResponse> => {
  const response = await apiClient.post<CheckEmailResponse>('/api/v1/auth/check-email', { email });
  return response.data;
};
export const signup = async (data: SignupRequest): Promise<SignupResponse> => {
  const response = await apiClient.post<SignupResponse>('/api/v1/auth/signup', data);
  return response.data;
};
export const verifyEmail = async (token: string): Promise<{ message: string }> => {
  const response = await apiClient.get<{ message: string }>(`/api/v1/auth/verify-email?token=${token}`);
  return response.data;
};
export const resendVerification = async (email: string): Promise<SignupResponse> => {
  const response = await apiClient.post<SignupResponse>('/api/v1/auth/resend-verification', { email });
  return response.data;
};

const authService = {
  logout,
  refreshToken,
  getCurrentUser,
  completeOnboarding,
  entraRedirectUrl,
  entraLogoutUrl,
  selectOrg,
  // Legacy — only referenced by unreachable pages
  login,
  changePassword,
  checkEmail,
  signup,
  verifyEmail,
  resendVerification,
};

export default authService;
